<template>
  <div :class="s[`d_${helper.displayDirection}`]">
    <ul :class="s.btns">
      <li
        v-for="item in btnItems"
        :key="item.value">
        <large-btn
          :color="item.color"
          :isClicked="flag.isActive === item.name"
          :disabled="!user.driverStatus.selectableStatus?.includes(item.name) || isDisabled(item)"
          @click="clicked(item)">{{ item.status }}</large-btn></li>
    </ul>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import cf from '@/mixins/commonFunctions.js';
import LargeBtn from './LargeBtn.vue';

export default {
  name: 'status-panel',
  mixins: [cf],
  components: {
    LargeBtn,
  },
  data() {
    return {
      flag: {
        isActive: null,
      },
    };
  },
  computed: {
    ...mapState(['user', 'page', 'helper']),
    btnItems() {
      const items = cf.getStatusItems(this.helper.master) || [];
      const homeOnlyActivities = ['closed', 'emergency'];
      let filtered = items.filter((item) => !homeOnlyActivities.includes(item.name));
      if (this.user.role.role === 11) filtered = filtered.filter((item) => item.name !== 'arrived');
      return filtered;
    },
  },
  watch: {
    user: {
      handler() {
        this.hideLoading();
      },
      deep: true,
    },
  },
  methods: {
    /** ローディング表示 */
    showLoading() {
      const args = { modalName: 'modalLoadingBallScaleRippleMultiple' };
      this.$store.dispatch('modal/loadings/showModal', args, { root: true });
    },

    /** ローディング終了 */
    hideLoading() {
      this.$store.dispatch('modal/loadings/hideModal', null, { root: true });
    },

    /** 例外的にボタンをdisabledにするための設定 */
    isDisabled(item) {
      // 迎車ボタンはorderがある時のみ有効
      return (item.name === 'reserved' && !this.user.driverStatus.orderId) || (item.name === 'waiting' && !this.user.taxiStand.length);
    },

    clicked(params) {
      // 押したように見せるUIのための処理
      this.flag.isActive = params.name;
      setTimeout(() => {
        this.flag.isActive = null;
      }, 200);

      // ここから処理振り分け
      const actionType = params.name;
      if (params.itemType === 'driverActivity' || actionType === 'empty') {
        // 空車状態から、待機・休憩に変更した時
        this.registWaitingDriver(actionType);
      } else if (this.user.driverStatus.name === 'empty' && actionType === 'occupied') {
        // 流しで乗車
        this.registOccupiedStatus();
      } else if (params.itemType === 'orderFlag') {
        if (this.user.driverStatus.isWaiting) {
          // 待機状態から迎車に変更した時
          this.registWaitingDriver(actionType);
        }
        const id = this.user.driverStatus.orderId;
        const helperOrders = this.helper.master.orders;
        const flag = helperOrders?.flags.find((hFlag) => hFlag.name === actionType)?.value || null;
        if (id && flag) this.orderUpdate({ id, flag });
      }
    },

    registWaitingDriver(type) {
      if (type === 'waiting' && !this.user.taxiStand.length) {
        alert('待機所が設定されていないため、状態を変更できません。');
        return;
      }
      // 現在のステータス確認・エンドポイント判定
      const userWaitingDriver = this.user.waitingDriver?.[0] || null;
      const endpoint = userWaitingDriver ? 'update' : 'create';

      // typeから、typeのvalueを特定
      let typeNum;
      if (userWaitingDriver) typeNum = userWaitingDriver.type; // 空車が押された場合
      else typeNum = this.helper.master.driverActivities?.items.find((activity) => activity.name === type)?.value || null;
      if (!typeNum) return; // 想定外

      this.showLoading();

      const data = {
        driver_id: this.user.id,
        type: typeNum,
      };
      if (userWaitingDriver) {
        data.id = userWaitingDriver.id;
        data.end_at = moment().format('YYYY-MM-DD HH:mm:ss');
      }

      this.axios({
        method: 'POST',
        url: `/v1/waitingDriver/${endpoint}`,
        data,
      })
        .then((response) => {
          const res = response.data;
          if (res.isNotInside) {
            alert('待機所にいないか、位置情報が処理中のため、待機状態に変更できません。');
            return;
          }
          if (res.existsValidOrder) {
            alert('有効な予約があるため、待機状態に変更できません。');
            return;
          }
          this.$store.dispatch('user/update');
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
        })
        .finally(() => {
          this.hideLoading();
        });
    },

    orderUpdate({ id, flag }) {
      this.showLoading();

      const data = { id, flag, revisionUserId: this.user.id };
      this.axios({
        method: 'POST',
        url: '/v1/order/update',
        data,
      })
        .then((response) => {
          const res = response.data;
          if (!res.updated) alert('予約ステータスの更新に失敗しました。最新の状態に更新します。');
        })
        .catch((error) => {
          console.log(error);
          alert('エラーが発生しました。再度お試しください。');
          this.hideLoading();
        })
        .finally(() => {
          this.$store.dispatch('user/update');
        });
    },

    async registOccupiedStatus() {
      this.showLoading();
      const data = {
        flag: 1,
        route: 3, // 3: driver
        driver_id: this.user.id,
      };

      this.axios({
        method: 'POST',
        url: '/v1/reservation/create',
        data,
      })
        .then((response) => {
          const res = response.data;
          if (res.reservationId) {
            this.createOrder(res.reservationId);
          } else {
            alert('予約の登録に失敗しました。\n再度お試しください。');
            this.hideLoading();
          }
        })
        .catch((error) => {
          alert('予約の登録に失敗しました。\n再度お試しください。');
          if (error.response) console.log(error.response.data);
          else console.log(error);
          this.hideLoading();
        });
    },

    createOrder(reservationId) {
      if (this.user.vehicles?.[0]?.flag !== 1) {
        alert('車両が選択されていないため、ステータスを変更するができませんでした。');
        this.hideLoading();
        return;
      }
      const data = {
        reservation_id: reservationId,
        driver_id: this.user.id,
        vehicle_id: this.user.vehicles?.[0]?.id,
      };
      this.axios({
        method: 'POST',
        url: '/v1/order/create',
        data,
      })
        .then(() => {
          this.$store.dispatch('user/update');
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
          this.hideLoading();
        });
    },
  },
};
</script>

<style lang="scss" module="s">
.d_horizontal {
  height: 100%;
  width: 100%;
  .btns {
    display: grid;
    grid-template-columns: repeat(2, var(--large-btn-w));
    grid-gap: var(--status-btn-padding);
    height: 100%;
    padding-top: var(--status-btn-padding);
    padding-bottom: var(--status-btn-padding);
    justify-content: center;
    > li {
      list-style: none;
      > button {
        height: 100%;
        width: 100%;
      }
    }
  }
}
.d_vertical {
  height: 100%;
  width: 100%;
  .btns {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 4px;
    height: 100%;
    padding: 1px;
    justify-content: center;
    > li {
      list-style: none;
      > button {
        height: 100%;
        min-height: 120px;
        width: 100%;
      }
    }
  }
}
</style>
